/**
 * Hooks for the timer feature. Centralised so the pill, popover, and
 * timesheet-page rail all share the same cache / invalidation logic.
 */
import type { Customer } from '@clockwise/clients/customers';
import type { Product } from '@clockwise/clients/products';
import type {
  CreateTimerInput,
  OpenTimers,
  PatchTimerInput,
  TimerView,
} from '@clockwise/clients/timers';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { customersApi, productsApi, timersApi } from '@/lib/api';
import { engagementRequiredCandidates } from '@/lib/engagement-required';
import { useMe } from '@/lib/me';
import { queryKeys } from '@/lib/query-keys';
import { signalCommittedEntry } from '@/lib/timesheet-commit-focus';

type OpenTimersResponse = { data: OpenTimers };

/**
 * Read the most-recent fetch time of the open-timers query out of the
 * TanStack Query cache. Pin live-elapsed math to the same moment the
 * server snapshot was taken so the pill/popover ticks without re-querying.
 */
export function useTimersFetchedAt(): Date | null {
  const qc = useQueryClient();
  const state = qc.getQueryState(queryKeys.timers.open());
  if (!state?.dataUpdatedAt) return null;
  return new Date(state.dataUpdatedAt);
}

/**
 * Resolve a customer name from a customerId. Used by the timer popover's
 * paused-card summary and the top-center TimerHud to label running and
 * paused timers without forcing the caller to manage its own fetch.
 *
 * 60s `staleTime` matches the open-timers poll cadence — customer names
 * effectively don't change between polls, and TanStack Query dedups
 * concurrent identical requests so multiple paused cards on the same
 * customer share a single in-flight call.
 */
export function useCustomerNameLookup(customerId: string | null): string | null {
  const q = useQuery({
    queryKey: customerId ? queryKeys.customers.detail(customerId) : ['customers', 'detail', 'none'],
    queryFn: () => customersApi.get(customerId as string),
    enabled: Boolean(customerId),
    staleTime: 60_000,
  });
  if (!customerId) return null;
  const data = q.data?.data as Customer | undefined;
  return data?.name ?? null;
}

/** Mirror of `useCustomerNameLookup` for products. */
export function useProductNameLookup(productId: string | null): string | null {
  const q = useQuery({
    queryKey: productId ? queryKeys.products.detail(productId) : ['products', 'detail', 'none'],
    queryFn: () => productsApi.get(productId as string),
    enabled: Boolean(productId),
    staleTime: 60_000,
  });
  if (!productId) return null;
  const data = q.data?.data as Product | undefined;
  return data?.name ?? null;
}

// Poll cadence for the open-timers list. The live elapsed counter ticks
// client-side (`useLiveElapsed`), so the poll only reconciles state changed
// elsewhere (other tabs/devices) and slides the auth-session cookie — neither
// needs a tight cadence. The old blanket 5s poll ran on every open tab and was
// a steady multiplier on backend request volume; a 20s foreground / 60s
// background split cuts that ~4–12× while keeping the UI fresh. The elapsed
// display is unaffected.
const FG_POLL_MS = 20_000;
const BG_POLL_MS = 60_000;

/** Poll the slower cadence when the tab is hidden, the faster one when visible. */
function openTimersPollInterval(): number {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return BG_POLL_MS;
  return FG_POLL_MS;
}

export function useOpenTimers() {
  // Gate on `timer.view`: timers are a per-role feature toggle, and the
  // API 403s when the (possibly impersonated) effective user lacks it.
  // Skipping the query keeps the HUD, popover, and timesheet grid from
  // firing a request that's guaranteed to fail. `useMe` is already loaded
  // at the authenticated layout boundary, so this is a cache read.
  const canView = useMe().permissions.has('timer.view');
  return useQuery({
    queryKey: queryKeys.timers.open(),
    queryFn: () => timersApi.listOpen(),
    enabled: canView,
    refetchInterval: openTimersPollInterval,
    // Keep polling when the tab is backgrounded (at the slower BG cadence) so
    // the better-auth session cookie keeps sliding forward (updateAge=5m,
    // expiresIn=8h). Without this, a backgrounded Clockwise tab would 401 on
    // its next foreground click after the cookie's absolute expiry. Idle-based
    // sign-out is enforced separately via SessionIdleWarning — server-side
    // session longevity and client-side idle policy are two different concerns.
    refetchIntervalInBackground: true,
  });
}

/** Convenience: spreads the running/paused split into top-level fields. */
export function useTimers() {
  const query = useOpenTimers();
  const data = query.data?.data;
  const running = data?.running ?? null;
  const paused = data?.paused ?? [];
  const pausedTotal = data?.pausedTotal ?? 0;
  const pausedLimit = data?.pausedLimit ?? 0;
  return { ...query, running, paused, pausedTotal, pausedLimit };
}

/**
 * Ticking elapsed-seconds for a running timer. `baseSeconds` is the server-
 * computed total at `baseAt`; this hook adds the wall-clock delta since
 * `baseAt` to produce a live count without re-querying every second.
 *
 * Pass `baseAt = null` when the snapshot timestamp isn't known yet (very
 * first render before the query resolves) — the hook returns `baseSeconds`
 * unchanged and doesn't tick. Without this guard, callers that fell back
 * to `?? new Date()` every render would create a fresh `Date` each render
 * and the elapsed math would jitter by render-time-ms.
 *
 * For paused timers, callers should pass `running = false` so the hook
 * doesn't tick.
 */
export function useLiveElapsed(baseSeconds: number, baseAt: Date | null, running: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running || baseAt == null) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [running, baseAt]);
  if (!running || baseAt == null) return baseSeconds;
  return baseSeconds + Math.max(0, Math.floor((now - baseAt.getTime()) / 1000));
}

interface MutationOpts<T> {
  onSuccess?: (data: T) => void;
}

/** Marker prefix for optimistic-placeholder timer ids. Card renderers
 *  check this to disable per-card actions until the server-issued id
 *  replaces the placeholder via cache invalidation. */
export const TEMP_TIMER_ID_PREFIX = 'temp:';
export const isTempTimerId = (id: string): boolean => id.startsWith(TEMP_TIMER_ID_PREFIX);

function placeholderRunningTimer(input: CreateTimerInput): TimerView {
  const nowIso = new Date().toISOString();
  return {
    timer: {
      id: `${TEMP_TIMER_ID_PREFIX}${crypto.randomUUID()}`,
      // Tenant/user ids are unknown client-side but never rendered on the
      // cards — the cache invalidate after start swaps the placeholder
      // out before anything could read them.
      tenantId: '',
      userId: '',
      customerId: input.customerId ?? null,
      productId: input.productId ?? null,
      engagementId: input.engagementId ?? null,
      // `workDate` is server-assigned in the user's timezone; an empty
      // string is the sentinel until the cache invalidate swaps in the
      // real workDate (any local guess can disagree with the user's
      // stored timezone around midnight). The form's membership /
      // in-scope-products queries gate on a non-empty workDate so this
      // sentinel never reaches the server as an empty (400-rejected)
      // fromDate/toDate.
      workDate: '',
      description: input.description ?? null,
      billable: input.billable ?? false,
      status: 'running',
      parentTimerId: null,
      createdAt: nowIso,
      updatedAt: nowIso,
    },
    elapsedSeconds: 0,
  };
}

function applyStartOptimistic(
  prev: OpenTimersResponse | undefined,
  placeholder: TimerView,
): OpenTimersResponse {
  const data = prev?.data;
  const prevRunning = data?.running ?? null;
  const prevPaused = data?.paused ?? [];
  const demoted: TimerView | null = prevRunning
    ? { ...prevRunning, timer: { ...prevRunning.timer, status: 'paused' } }
    : null;
  // `pausedLimit` is server-owned: if we have a prior snapshot we
  // preserve it; otherwise omit (= 0) and let the onSettled invalidate
  // fill the authoritative value rather than re-asserting a magic
  // default that could drift from the server.
  return {
    data: {
      running: placeholder,
      paused: demoted ? [demoted, ...prevPaused] : prevPaused,
      pausedTotal: (data?.pausedTotal ?? 0) + (demoted ? 1 : 0),
      pausedLimit: data?.pausedLimit ?? 0,
    },
  };
}

/** Apply a field patch to the matching timer (running or paused) in the
 *  cached open-timers snapshot. Used as the optimistic projection for
 *  `usePatchTimer` so customer/product/description edits reflect in the
 *  same frame as the click instead of waiting on the PATCH + refetch
 *  round-trips (the two sequential ~800ms hits that made the picker feel
 *  multi-second slow). */
function applyPatchOptimistic(
  prev: OpenTimersResponse,
  timerId: string,
  input: PatchTimerInput,
): OpenTimersResponse {
  if (!prev?.data) return prev;
  const { running, paused } = prev.data;
  const merge = (v: TimerView): TimerView => ({ ...v, timer: { ...v.timer, ...input } });
  if (running?.timer.id === timerId) {
    return { ...prev, data: { ...prev.data, running: merge(running) } };
  }
  if (paused.some((v) => v.timer.id === timerId)) {
    return {
      ...prev,
      data: { ...prev.data, paused: paused.map((v) => (v.timer.id === timerId ? merge(v) : v)) },
    };
  }
  return prev;
}

/** Strip a timer (by id) from both running and paused buckets. Used as
 *  the optimistic projection for commit and discard — both transition a
 *  timer out of the open list. */
function applyRemoveOptimistic(prev: OpenTimersResponse, timerId: string): OpenTimersResponse {
  if (!prev?.data) return prev;
  const { running, paused, pausedTotal } = prev.data;
  const wasRunning = running?.timer.id === timerId;
  const wasPaused = paused.some((v) => v.timer.id === timerId);
  if (!wasRunning && !wasPaused) return prev;
  return {
    ...prev,
    data: {
      ...prev.data,
      running: wasRunning ? null : running,
      paused: wasPaused ? paused.filter((v) => v.timer.id !== timerId) : paused,
      pausedTotal: wasPaused ? Math.max(0, pausedTotal - 1) : pausedTotal,
    },
  };
}

export function useStartTimer(opts: MutationOpts<TimerView> = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTimerInput) => timersApi.start(input),
    // Optimistically inject a running placeholder so the popover updates
    // in the same frame as the click. The placeholder carries a
    // `temp:` id; per-card actions ignore it (see `isTempTimerId`)
    // until the server reply lands and the cache invalidate swaps in
    // the real timer.
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: queryKeys.timers.open() });
      const snapshot = qc.getQueryData<OpenTimersResponse>(queryKeys.timers.open());
      const placeholder = placeholderRunningTimer(input);
      qc.setQueryData<OpenTimersResponse>(
        queryKeys.timers.open(),
        applyStartOptimistic(snapshot, placeholder),
      );
      return { snapshot };
    },
    onSuccess: (res) => {
      toast.success('Timer started.');
      opts.onSuccess?.(res.data);
    },
    onError: (err, _input, ctx) => {
      if (ctx?.snapshot) qc.setQueryData(queryKeys.timers.open(), ctx.snapshot);
      toast.error(err instanceof Error ? err.message : 'Failed to start timer');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: queryKeys.timers.all }),
  });
}

/**
 * Optimistically flip the running timer (if any) to paused, and the target
 * timer (when it's the paused-but-targeted resume case below) to running.
 * Server response or refetch reconciles.
 */
function applyPauseOptimistic(prev: OpenTimersResponse, timerId: string): OpenTimersResponse {
  if (!prev?.data) return prev;
  const { running, paused } = prev.data;
  if (!running || running.timer.id !== timerId) return prev;
  // Stop the live ticking client-side immediately by flipping status.
  const newPaused: TimerView = {
    ...running,
    timer: { ...running.timer, status: 'paused' },
  };
  return {
    ...prev,
    data: { ...prev.data, running: null, paused: [newPaused, ...paused] },
  };
}

function applyResumeOptimistic(prev: OpenTimersResponse, timerId: string): OpenTimersResponse {
  if (!prev?.data) return prev;
  const { running, paused } = prev.data;
  const target = paused.find((v) => v.timer.id === timerId);
  if (!target) return prev;
  // Demote the current running (if any) to paused; promote target to running.
  const demoted: TimerView | null = running
    ? { ...running, timer: { ...running.timer, status: 'paused' } }
    : null;
  const nextPaused = paused.filter((v) => v.timer.id !== timerId);
  if (demoted) nextPaused.unshift(demoted);
  return {
    ...prev,
    data: {
      ...prev.data,
      running: { ...target, timer: { ...target.timer, status: 'running' } },
      paused: nextPaused,
    },
  };
}

export function usePauseTimer() {
  const qc = useQueryClient();
  return useMutation({
    // Serialize per-timer pause/resume mutations so rapid clicks land in
    // user-intent order. Without this, HTTP/2 multiplexing or any backend
    // reorder can land the final state opposite of the user's last click
    // (e.g., click Pause then Resume → server processes Resume first,
    // then Pause, end state is paused — wrong). H6.
    scope: { id: 'timer-pause-resume' },
    mutationFn: (id: string) => timersApi.pause(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: queryKeys.timers.open() });
      const snapshot = qc.getQueryData<OpenTimersResponse>(queryKeys.timers.open());
      if (snapshot) {
        qc.setQueryData<OpenTimersResponse>(
          queryKeys.timers.open(),
          applyPauseOptimistic(snapshot, id),
        );
      }
      return { snapshot };
    },
    onError: (err, _id, ctx) => {
      // Rollback to the pre-mutation snapshot. The error toast fires after.
      if (ctx?.snapshot) qc.setQueryData(queryKeys.timers.open(), ctx.snapshot);
      toast.error(err instanceof Error ? err.message : 'Failed to pause timer');
    },
    // Always invalidate after settle so the server's elapsed_seconds
    // (computed since last open interval) replaces the optimistic value.
    onSettled: () => qc.invalidateQueries({ queryKey: queryKeys.timers.all }),
  });
}

export function useResumeTimer() {
  const qc = useQueryClient();
  return useMutation({
    // Same scope as `usePauseTimer` so pause and resume serialize
    // through TanStack Query — see H6 note there.
    scope: { id: 'timer-pause-resume' },
    mutationFn: (id: string) => timersApi.resume(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: queryKeys.timers.open() });
      const snapshot = qc.getQueryData<OpenTimersResponse>(queryKeys.timers.open());
      if (snapshot) {
        qc.setQueryData<OpenTimersResponse>(
          queryKeys.timers.open(),
          applyResumeOptimistic(snapshot, id),
        );
      }
      return { snapshot };
    },
    onError: (err, _id, ctx) => {
      if (ctx?.snapshot) qc.setQueryData(queryKeys.timers.open(), ctx.snapshot);
      toast.error(err instanceof Error ? err.message : 'Failed to resume timer');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: queryKeys.timers.all }),
  });
}

export function usePatchTimer() {
  const qc = useQueryClient();
  return useMutation({
    // Serialize patches so rapid field edits (e.g. two quick billable
    // toggles, or a customer-change patch racing a billable toggle) land in
    // user-intent order. Each patch derives its next value from the current
    // snapshot; without serialization HTTP/2 multiplexing or a backend reorder
    // can commit them out of order and the onSettled refetch then pins the
    // wrong final value (a silent lost-update). Same H6 fix pause/resume use.
    scope: { id: 'timer-patch' },
    mutationFn: ({ id, input }: { id: string; input: PatchTimerInput }) =>
      timersApi.patch(id, input),
    // Optimistically merge the patched fields into the cached timer so the
    // picker reflects the selection in the same frame as the click. Without
    // this the field stays stale (and the form stays disabled via
    // `patch.isPending`) until the PATCH + the onSettled refetch both land —
    // two sequential ~800ms round-trips, which is what made adding a
    // customer/product feel like it took seconds.
    onMutate: async ({ id, input }) => {
      await qc.cancelQueries({ queryKey: queryKeys.timers.open() });
      const snapshot = qc.getQueryData<OpenTimersResponse>(queryKeys.timers.open());
      if (snapshot) {
        qc.setQueryData<OpenTimersResponse>(
          queryKeys.timers.open(),
          applyPatchOptimistic(snapshot, id, input),
        );
      }
      return { snapshot };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.snapshot) qc.setQueryData(queryKeys.timers.open(), ctx.snapshot);
      toast.error(err instanceof Error ? err.message : 'Failed to update timer');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: queryKeys.timers.all }),
  });
}

export function useCommitTimer() {
  const qc = useQueryClient();
  return useMutation({
    // The committed entry's role is the timer activity's own role (resolved
    // server-side from the activity), so no role hint is sent.
    mutationFn: (vars: { id: string }) => timersApi.commit(vars.id),
    onMutate: async ({ id }) => {
      await qc.cancelQueries({ queryKey: queryKeys.timers.open() });
      const snapshot = qc.getQueryData<OpenTimersResponse>(queryKeys.timers.open());
      if (snapshot) {
        qc.setQueryData<OpenTimersResponse>(
          queryKeys.timers.open(),
          applyRemoveOptimistic(snapshot, id),
        );
      }
      return { snapshot };
    },
    onSuccess: (res) => {
      // Commit creates a timesheet row — refresh that side too.
      qc.invalidateQueries({ queryKey: queryKeys.timesheets.all });
      // Tell the timesheet page which row was just produced so it can scroll
      // it into view (and hop to its week/day) once the refetch lands.
      signalCommittedEntry(res.data.timesheet);
      toast.success('Timer committed to timesheet.');
    },
    // Server-side validation errors (missing customer/product) surface with
    // their own field-error envelope; the global MutationCache toast handles
    // those. We keep a fallback here for non-field errors.
    onError: (err, _vars, ctx) => {
      if (ctx?.snapshot) qc.setQueryData(queryKeys.timers.open(), ctx.snapshot);
      // ENGAGEMENT_REQUIRED is answered by the caller (it asks which engagement
      // and retries) — not a failure to toast.
      if (engagementRequiredCandidates(err)?.length) return;
      toast.error(err instanceof Error ? err.message : 'Failed to commit timer');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: queryKeys.timers.all }),
  });
}

/**
 * Bulk-commit hook: fires N parallel commits and reports a combined
 * outcome. Optimistically removes each successfully-targeted timer
 * from the open list before the requests resolve so the popover
 * empties out in one tick. Server errors per-id surface as a single
 * aggregate toast — the cache invalidate at the end re-fetches the
 * authoritative state so any rollbacks are implicit.
 */
export function useCommitTimers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: readonly string[]) => {
      const results = await Promise.allSettled(ids.map((id) => timersApi.commit(id)));
      let failed = 0;
      // Refused because several engagements cover the timer and none was
      // picked. Counted apart because the fix is different: open that timer
      // and pick one, rather than retry.
      let needEngagement = 0;
      for (const r of results) {
        if (r.status !== 'rejected') continue;
        failed += 1;
        if (engagementRequiredCandidates(r.reason)?.length) needEngagement += 1;
      }
      return { total: ids.length, failed, needEngagement };
    },
    onMutate: async (ids) => {
      await qc.cancelQueries({ queryKey: queryKeys.timers.open() });
      const snapshot = qc.getQueryData<OpenTimersResponse>(queryKeys.timers.open());
      if (snapshot) {
        let next = snapshot;
        for (const id of ids) next = applyRemoveOptimistic(next, id);
        qc.setQueryData<OpenTimersResponse>(queryKeys.timers.open(), next);
      }
      return { snapshot };
    },
    onSuccess: ({ total, failed, needEngagement }) => {
      qc.invalidateQueries({ queryKey: queryKeys.timesheets.all });
      const ok = total - failed;
      if (failed === 0) {
        toast.success(`Committed ${ok} timer${ok === 1 ? '' : 's'}.`);
        return;
      }
      const parts: string[] = [];
      if (ok > 0) parts.push(`Committed ${ok}`);
      if (needEngagement > 0) {
        parts.push(
          `${needEngagement} need${needEngagement === 1 ? 's' : ''} an engagement picked — commit ${needEngagement === 1 ? 'it' : 'them'} individually`,
        );
      }
      if (failed > needEngagement) parts.push(`${failed - needEngagement} failed`);
      toast.error(parts.join(' · '));
    },
    onError: (err, _ids, ctx) => {
      if (ctx?.snapshot) qc.setQueryData(queryKeys.timers.open(), ctx.snapshot);
      toast.error(err instanceof Error ? err.message : 'Failed to commit timers');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: queryKeys.timers.all }),
  });
}

export function useDiscardTimer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => timersApi.discard(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: queryKeys.timers.open() });
      const snapshot = qc.getQueryData<OpenTimersResponse>(queryKeys.timers.open());
      if (snapshot) {
        qc.setQueryData<OpenTimersResponse>(
          queryKeys.timers.open(),
          applyRemoveOptimistic(snapshot, id),
        );
      }
      return { snapshot };
    },
    onSuccess: () => {
      toast.success('Timer discarded.');
    },
    onError: (err, _id, ctx) => {
      if (ctx?.snapshot) qc.setQueryData(queryKeys.timers.open(), ctx.snapshot);
      toast.error(err instanceof Error ? err.message : 'Failed to discard timer');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: queryKeys.timers.all }),
  });
}

/** Format elapsed seconds as `HH:MM:SS` (always padded for layout stability). */
export function formatElapsed(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

/** Format elapsed seconds as a compact `Xh Ym` ("0h 23m" / "2h 5m"). */
export function formatElapsedCompact(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

/**
 * Format a duration as a short human string — `1h 4m`, `4m 11s`, `11s`.
 * Deliberately unlike the `HH:MM:SS` clock so a summed total (e.g. across
 * open timers) doesn't read as another ticking counter. Seconds are shown
 * only under an hour, where a live value still visibly advances; past an
 * hour they'd just add churn, so it settles to `Xh Ym`.
 */
export function formatDurationShort(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}
